<script setup>
  import { ref, computed, onMounted, watch } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useMasterReportsStore } from "@stores/masterReports/masterReports";
  import { usePptxImportStore } from "@stores/pptxImport";
  import { useAccountsStore } from "@stores/accounts";
  import useMyFetch from "@stores/useMyFetch";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import MasterReportForm from "@/components/Reports/MasterReports/MasterReportForm.vue";

  const route = useRoute();
  const router = useRouter();

  const masterReportsStore = useMasterReportsStore();
  const pptxImportStore = usePptxImportStore();
  const accountsStore = useAccountsStore();

  const { currentMasterReport, loading } = storeToRefs(masterReportsStore);
  const { initEditState, setEditedName, setEditedActive, setEditedAccountIds, setEditedIsSystem, setEditedIsQualitative } = masterReportsStore;

  const accountOptions = computed(() =>
    (accountsStore.accounts ?? []).map((a) => ({ value: String(a.id), label: a.name })),
  );

  const themeToken = computed(() => route.params.themeToken);

  const pptxSource = ref(null);
  const importing = ref(false);
  const importError = ref(null);
  const formRef = ref(null);

  onMounted(async () => {
    accountsStore.fetch();
    await masterReportsStore.fetchMasterReport(themeToken.value);
    if (currentMasterReport.value) {
      initEditState();
      pptxImportStore.setThemeToken(themeToken.value);
      if (currentMasterReport.value.pptx_source_id) {
        await fetchPptxSource(currentMasterReport.value.pptx_source_id);
      }
    }
  });

  watch(
    () => currentMasterReport.value?.pptx_source_id,
    async (newSourceId) => {
      if (newSourceId) {
        await fetchPptxSource(newSourceId);
      } else {
        pptxSource.value = null;
      }
    },
  );

  async function fetchPptxSource(sourceId) {
    const { response, data } = await useMyFetch(`/master_reports/${themeToken.value}/pptx_imports/${sourceId}`)
      .get()
      .json();
    if (response.value.ok) {
      pptxSource.value = { id: sourceId, filename: data.value.filename };
    }
  }

  function downloadPptx() {
    window.location.href = `/api/master_reports/${themeToken.value}/download`;
  }

  function handleNameChange(name) {
    setEditedName(name);
  }

  function handleActiveChange(value) {
    setEditedActive(value);
  }

  function handleAccountsChange(value) {
    setEditedAccountIds(value);
  }

  function handleIsSystemChange(value) {
    setEditedIsSystem(value);
  }

  function handleQualitativeChange(value) {
    setEditedIsQualitative(value);
  }

  async function handleImport({ file }) {
    importing.value = true;
    importError.value = null;
    try {
      const success = await pptxImportStore.analyze(file);
      if (success) {
        formRef.value?.reset();
        router.push({ name: "MasterReportSlides", params: { themeToken: themeToken.value } });
      } else {
        importError.value = pptxImportStore.analyzeError || "Failed to import PPTX";
      }
    } catch (e) {
      importError.value = e.message || "Failed to import PPTX";
    } finally {
      importing.value = false;
    }
  }
</script>

<template>
  <div class="flex justify-center p-4">
    <LoadingState v-if="loading" />

    <MasterReportForm
      v-else-if="currentMasterReport"
      ref="formRef"
      mode="edit"
      :initial-name="currentMasterReport.name"
      :initial-active="currentMasterReport.active ?? true"
      :initial-account-ids="currentMasterReport.account_ids ?? []"
      :initial-is-system="currentMasterReport.is_system ?? false"
      :account-options="accountOptions"
      :initial-qualitative="currentMasterReport.is_qualitative ?? false"
      :pptx-source="pptxSource"
      :submitting="importing"
      :error="importError"
      @submit="handleImport"
      @download="downloadPptx"
      @update:name="handleNameChange"
      @update:active="handleActiveChange"
      @update:account-ids="handleAccountsChange"
      @update:is-system="handleIsSystemChange"
      @update:qualitative="handleQualitativeChange" />
  </div>
</template>
